import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import './AdvancedDataTable.css'; // Estilos personalizados para integración con design system

// Registrar módulos de AG Grid (Community)
ModuleRegistry.registerModules([ AllCommunityModule ]);

const AdvancedDataTable = ({
  data = [],
  columns = [],
  loading = false,
  emptyMessage = 'No hay datos para mostrar',
  pagination = true,
  pageSize = 100,
  pageSizeOptions = [30, 100, 500],
  searchable = true,
  exportable = true,
  onRowClick = null,
  height = '60vh',
  className = '',
  marketplace = '',
  ...props
}) => {
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [currentTheme, setCurrentTheme] = useState('light');

  // Detectar tema actual
  useEffect(() => {
    const detectTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme') || 'light';
      setCurrentTheme(theme);
    };

    detectTheme();
    
    // Observar cambios en el tema
    const observer = new MutationObserver(detectTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    
    return () => observer.disconnect();
  }, []);

  // Configurar columnas para AG Grid
  const columnDefs = useMemo(() => {
    // COLUMNA DE ÍNDICE - Siempre primera columna
    const indexColumn = {
      headerName: '#',
      field: '__index__',
      colId: '__index__',
      valueGetter: (params) => params.node.rowIndex + 1,
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      lockPosition: true,
      suppressMovable: true,
      cellClass: 'index-column-cell',
      headerClass: 'index-column-header'
    };
    
    if (columns.length > 0) {
      // Aceptar columnas como strings o como objetos; normalizar a defs válidos
      const normalized = columns.map((col) => {
        if (typeof col === 'string') {
          const header = col.charAt(0).toUpperCase() + col.slice(1).replace(/_/g, ' ');
          return {
            field: col,
            headerName: header,
            sortable: true,
            filter: true,
            resizable: true,
            width: col.includes('Hist') || col.includes('Pred') ? 120 : (col === 'product_name' ? 250 : 150),
            minWidth: 100,
            valueFormatter: (params) => {
              const value = params.value;
              const field = params.colDef.field || '';
              if (value === null || value === undefined || value === '') return '-';
              if (value === 'NO ENCONTRADO' || value === 'NO NEWTON') return value;
              if (typeof value === 'number') {
                if (['stock_actual','stock_transito','stock_final','venta_ultimos_30','sugerencia_resurtido'].includes(field)) {
                  try { return value.toLocaleString(); } catch { return String(value); }
                }
                if (field.startsWith('u_') || field === 'pred' || field.includes('Hist') || field.includes('Pred')) {
                  try { return Number.isFinite(value) ? value.toFixed(0) : String(value); } catch { return String(value); }
                }
                try { return Number.isFinite(value) ? value.toFixed(2) : String(value); } catch { return String(value); }
              }
              return String(value);
            }
          };
        }
        // Si viene objeto, asegurar que tenga 'field' y 'headerName'
        const fieldName = col.key || col.field;
        const header = col.title || col.headerName || (fieldName ? (fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/_/g, ' ')) : undefined);
        return {
          field: fieldName,
          headerName: header,
          sortable: col.sortable !== false,
          filter: col.filter !== false,
          resizable: col.resizable !== false,
          width: typeof col.width === 'number' ? col.width : (typeof col.width === 'string' ? parseInt(col.width) : 150),
          minWidth: col.minWidth || 100,
          cellRenderer: col.cellRenderer || null,
          valueFormatter: col.valueFormatter || ((params) => {
            const value = params.value;
            const field = params.colDef.field || '';
            if (value === null || value === undefined || value === '') return '-';
            if (value === 'NO ENCONTRADO' || value === 'NO NEWTON') return value;
            if (typeof value === 'number') {
              if (['stock_actual','stock_transito','stock_final','venta_ultimos_30','sugerencia_resurtido'].includes(field)) {
                try { return value.toLocaleString(); } catch { return String(value); }
              }
              if (field.startsWith('u_') || field === 'pred' || field.includes('Hist') || field.includes('Pred')) {
                try { return Number.isFinite(value) ? value.toFixed(0) : String(value); } catch { return String(value); }
              }
              try { return Number.isFinite(value) ? value.toFixed(2) : String(value); } catch { return String(value); }
            }
            return String(value);
          }),
          ...col
        };
      }).filter(def => !!def.field); // descartar entradas inválidas

      // Si después de normalizar no hay fields válidos, inferir desde data
      if (normalized.length === 0 && data.length > 0) {
        const sampleRow = data[0];
        const inferredColumns = Object.keys(sampleRow).map(key => ({
          field: key,
          headerName: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
          sortable: true,
          filter: true,
          resizable: true,
          width: key.includes('Hist') || key.includes('Pred') ? 120 : (key === 'product_name' ? 250 : 150),
          minWidth: 100,
        }));
        return [indexColumn, ...inferredColumns];
      }

      return [indexColumn, ...normalized];
    }

    // Auto-generar columnas si no se proporcionan
    if (data.length === 0) return [indexColumn];

    const sampleRow = data[0];
    const autoGeneratedColumns = Object.keys(sampleRow).map(key => {
      const isNumeric = typeof sampleRow[key] === 'number';
      const isHistorical = key.includes('Hist') || key.includes('Pred');
      const isInventory = ['stock_actual', 'stock_transito', 'stock_final', 'venta_ultimos_30', 'sugerencia_resurtido'].includes(key);
      
      return {
        field: key,
        headerName: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
        sortable: true,
        filter: true,
        resizable: true,
        width: isHistorical ? 120 : (key === 'product_name' ? 250 : 150),
        minWidth: 100,
        valueFormatter: (params) => {
          const value = params.value;
          const field = params.colDef.field || '';
          if (value === null || value === undefined || value === '') return '-';
          if (value === 'NO ENCONTRADO' || value === 'NO NEWTON') return String(value);
          if (typeof value === 'number') {
            if (['stock_actual','stock_transito','stock_final','venta_ultimos_30','sugerencia_resurtido'].includes(field)) {
              try { return value.toLocaleString(); } catch { return String(value); }
            }
            if (field.startsWith('u_') || field === 'pred' || field.includes('Hist') || field.includes('Pred')) {
              try { return Number.isFinite(value) ? value.toFixed(0) : String(value); } catch { return String(value); }
            }
            try { return Number.isFinite(value) ? value.toFixed(2) : String(value); } catch { return String(value); }
          }
          return String(value);
        },
        cellRenderer: (params) => {
          const value = params.value;
          
          if (value === null || value === undefined || value === '') {
            return '-';
          }
          
          // Formatear valores NO ENCONTRADO
          if (value === 'NO ENCONTRADO' || value === 'NO NEWTON') {
            return `<span style="color: #6b7280; font-style: italic;">${value}</span>`;
          }
          
          // Formatear valores numéricos
          if (isNumeric && typeof value === 'number') {
            if (isInventory) {
              return value.toLocaleString();
            }
            if (isHistorical) {
              return value.toFixed(2);
            }
            return value.toFixed(2);
          }
          
          // Truncar texto largo
          if (typeof value === 'string' && value.length > 50) {
            return `<span title="${value}">${value.substring(0, 50)}...</span>`;
          }
          
          return value;
        }
      };
    });
    
    return [indexColumn, ...autoGeneratedColumns];
  }, [data, columns]);

  // Configuración por defecto de AG Grid
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 0,
    minWidth: 100,
  }), []);

  // Configuración de la grid
  const gridOptions = useMemo(() => ({
    pagination: pagination,
    paginationPageSize: currentPageSize,
    paginationPageSizeSelector: pageSizeOptions,
    suppressScrollOnNewData: true,
    rowSelection: { mode: 'singleRow', enableClickSelection: true },
    animateRows: true,
    rowHeight: 35,
    headerHeight: 40,
    suppressMenuHide: true,
  }), [pagination, currentPageSize, pageSizeOptions]);

  // Callbacks de eventos
  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
  }, []);

  const onRowClicked = useCallback((event) => {
    if (onRowClick) {
      onRowClick(event.data, event.rowIndex);
    }
  }, [onRowClick]);

  // Función de búsqueda rápida (mejorada)
  const onQuickFilterChanged = useCallback((event) => {
    const value = event.target.value;
    setSearchText(value);
    if (gridApi) {
      // Método robusto: setGridOption es más reciente que setQuickFilter
      try {
        gridApi.setGridOption('quickFilterText', value);
      } catch {
        // Fallback para versiones anteriores
        gridApi.setQuickFilter(value);
      }
    }
  }, [gridApi]);

  // Función para exportar CSV
  const exportToCsv = useCallback(() => {
    if (gridApi) {
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `tabla_pivot_${marketplace}_${timestamp}.csv`;
      
      gridApi.exportDataAsCsv({
        fileName: filename,
        columnSeparator: ',',
        suppressQuotes: false,
        skipColumnHeaders: false,
        skipColumnGroupHeaders: false,
        allColumns: false,
        onlySelected: false
      });
    }
  }, [gridApi, marketplace]);

  // Función para ajustar columnas
  const autoSizeAllColumns = useCallback(() => {
    if (columnApi) {
      columnApi.autoSizeAllColumns();
    }
  }, [columnApi]);

  // Estado de carga
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: height,
        backgroundColor: 'var(--color-background)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--border-radius-lg)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ marginBottom: 'var(--spacing-md)' }} />
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>Cargando datos...</span>
        </div>
      </div>
    );
  }

  // Estado vacío
  if (!data || data.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: height,
        backgroundColor: 'var(--color-background)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--border-radius-lg)',
        textAlign: 'center',
        color: 'var(--color-text-secondary)'
      }}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={`advanced-data-table ${className}`} style={{ width: '100%' }}>
      {/* Barra de herramientas */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--spacing-md) var(--spacing-lg)',
        backgroundColor: 'var(--color-background)',
        border: '1px solid var(--color-border)',
        borderBottom: 'none',
        borderRadius: 'var(--border-radius-lg) var(--border-radius-lg) 0 0',
        flexWrap: 'wrap',
        gap: 'var(--spacing-md)',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-lg)', flexWrap: 'wrap' }}>
          <h3 style={{
            margin: 0,
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)'
          }}>
            📊 Tabla Pivot
            <span style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-normal)',
              color: 'var(--color-text-secondary)',
              backgroundColor: 'var(--color-primary-blue)',
              padding: 'var(--spacing-xs) var(--spacing-sm)',
              borderRadius: 'var(--border-radius-full)',
              color: 'var(--color-white)'
            }}>
              {data.length} registros
            </span>
          </h3>

          {searchable && (
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="🔍 Búsqueda rápida..."
                value={searchText}
                onChange={onQuickFilterChanged}
                style={{
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--border-radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text)',
                  width: '200px',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--color-primary-blue)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(41, 181, 232, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--color-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          {/* Selector de página */}
          <select
            value={currentPageSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value);
              setCurrentPageSize(newSize);
              if (gridApi) {
                gridApi.paginationSetPageSize(newSize);
              }
            }}
            style={{
              padding: 'var(--spacing-xs) var(--spacing-sm)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--border-radius-sm)',
              fontSize: 'var(--font-size-sm)',
              backgroundColor: 'var(--color-background)',
              color: 'var(--color-text)',
              cursor: 'pointer'
            }}
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>{size} filas</option>
            ))}
          </select>

          {/* Botón de ajustar columnas */}
          <button
            onClick={autoSizeAllColumns}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-xs)',
              padding: 'var(--spacing-sm) var(--spacing-md)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--border-radius-sm)',
              backgroundColor: 'var(--color-background)',
              color: 'var(--color-text)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: 'var(--font-weight-medium)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-light-gray)';
              e.target.style.borderColor = 'var(--color-primary-blue)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-background)';
              e.target.style.borderColor = 'var(--color-border)';
            }}
          >
            📏 Ajustar
          </button>

          {/* Botón de exportar */}
          {exportable && (
            <button
              onClick={exportToCsv}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                border: 'none',
                borderRadius: 'var(--border-radius-sm)',
                backgroundColor: 'var(--color-primary-blue)',
                color: 'var(--color-white)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontWeight: 'var(--font-weight-semibold)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--color-primary-dark-blue)';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = 'var(--shadow-md)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--color-primary-blue)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              📥 Descargar CSV
            </button>
          )}
        </div>
      </div>

      {/* Grid principal */}
      <div
        className={currentTheme === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
        style={{
          height: height,
          width: '100%',
          border: '1px solid var(--color-border)',
          borderRadius: '0 0 var(--border-radius-lg) var(--border-radius-lg)',
          overflow: 'hidden',
          backgroundColor: currentTheme === 'dark' ? '#151a21' : '#FFFFFF'
        }}
      >
        <AgGridReact
          rowData={data}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          {...props}
        />
      </div>
    </div>
  );
};

export default AdvancedDataTable;
